import { getRange, parseDateString, toDateString } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeesAllRoute } from "../../app-routes";
import type { EmployeesAllLayoutRoute } from "./employees-all.route";
import { balancesQuery, balancesYearRange } from "./ledgers.queries";

interface DateSearch {
    date?: DateString;
}

const YEAR_TABS = ["time", "vacation", "bonuses"] as const;
type YearTab = (typeof YEAR_TABS)[number];

const validateDateSearch = (search: Record<string, unknown>): DateSearch => ({
    date: search.date as DateString | undefined,
});

const hasValidDate = (search: DateSearch) => !!search.date && !!parseDateString(search.date);

function createLedgersMonthRoute(parent: EmployeesAllLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "ledgers-month",
        staticData: {
            title: "Konten: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/konten",
            menu: employeesAllRoute("ledgers-month"),
        },
        validateSearch: validateDateSearch,
        beforeLoad: ({ search }): void => {
            if (!hasValidDate(search)) {
                redirectTo("/employees/all/ledgers-month", { ...search, date: toDateString(new Date()) });
            }
        },
        loaderDeps: ({ search }) => ({ date: search.date! }),
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({
                ...balancesQuery(context.app, getRange(deps.date, "month")),
                staleTime: 30_000,
            }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-ledgers-all-month
                class="fullbleed"
                .balances=${match.loaderData}
                .date=${match.search.date}
            ></ptc-employees-ledgers-all-month>`,
    }).lazy(lazyElement(() => import("./employees-ledgers-all-month")));
}

function createLedgersYearRoutes(parent: EmployeesAllLayoutRoute) {
    const staticData = {
        title: "Konten: Alle Mitarbeiter",
        helpPage: "/handbuch/mitarbeiter/konten",
    };

    const bare = createRoute({
        getParentRoute: () => parent,
        path: "ledgers-year",
        staticData,
        validateSearch: validateDateSearch,
        beforeLoad: ({ search }): void => {
            redirectTo("/employees/all/ledgers-year/time", search);
        },
    });

    const tab = createRoute({
        getParentRoute: () => parent,
        path: "ledgers-year/$tab",
        staticData,
        validateSearch: validateDateSearch,
        beforeLoad: ({ search, params }): void => {
            if (!(YEAR_TABS as readonly string[]).includes(params.tab)) {
                redirectTo("/employees/all/ledgers-year/time", search);
            }
            if (!hasValidDate(search)) {
                redirectTo(`/employees/all/ledgers-year/${params.tab}`, {
                    ...search,
                    date: toDateString(new Date()),
                });
            }
        },
        loaderDeps: ({ search }) => ({ date: search.date! }),
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({
                ...balancesQuery(context.app, balancesYearRange(deps.date)),
                staleTime: 30_000,
            }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-ledgers-all-year
                class="fullbleed"
                .balances=${match.loaderData}
                .date=${match.search.date}
                .tab=${match.params.tab as YearTab}
            ></ptc-employees-ledgers-all-year>`,
    }).lazy(lazyElement(() => import("./employees-ledgers-all-year")));

    return [bare, tab] as const;
}

function createContractsRoute(parent: EmployeesAllLayoutRoute) {
    const staticData = {
        title: "Verträge: Alle Mitarbeiter",
        helpPage: "/handbuch/mitarbeiter/vertrag",
    };

    const bare = createRoute({
        getParentRoute: () => parent,
        path: "contracts",
        staticData: { ...staticData, menu: employeesAllRoute("contracts") },
        validateSearch: validateDateSearch,
        beforeLoad: ({ search }): void => {
            redirectTo("/employees/all/contracts/list", search);
        },
    });

    const page = createRoute({
        getParentRoute: () => parent,
        path: "contracts/$page",
        staticData,
        validateSearch: validateDateSearch,
        beforeLoad: ({ search, params }): void => {
            if (params.page !== "list" && params.page !== "changes") {
                redirectTo("/employees/all/contracts/list", search);
            }
            if (!hasValidDate(search)) {
                redirectTo(`/employees/all/contracts/${params.page}`, { ...search, date: toDateString(new Date()) });
            }
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-contracts-all
                class="fullbleed"
                .date=${match.search.date}
                .page=${match.params.page as "list" | "changes"}
            ></ptc-employees-contracts-all>`,
    }).lazy(lazyElement(() => import("./employees-contracts-all")));

    return [bare, page] as const;
}

export function createLedgersRoutes(parent: EmployeesAllLayoutRoute) {
    return [
        createLedgersMonthRoute(parent),
        ...createLedgersYearRoutes(parent),
        ...createContractsRoute(parent),
    ] as const;
}
